import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Package, User, Calendar, Edit, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { formatDate } from '../../utils';
import retiroCopiasService from '../../services/retiroCopiasService';
import { EditRetiroDialog } from './EditRetiroDialog';
import { DeleteRetiroConfirmDialog } from './DeleteRetiroConfirmDialog';
import { SignatureThumbnailCard } from './SignatureThumbnailCard';
import { SignaturePreviewDialog } from './SignaturePreviewDialog';
import type { Retirador, RetiroCopia } from '../../types';

interface RetiroHistorialListProps {
  otroTramiteId: string;
  copiasDisponibles: number; // Copias disponibles del trámite
}

export const RetiroHistorialList: React.FC<RetiroHistorialListProps> = ({
  otroTramiteId,
  copiasDisponibles,
}) => {
  const [editingRetiro, setEditingRetiro] = useState<RetiroCopia | null>(null);
  const [deletingRetiro, setDeletingRetiro] = useState<RetiroCopia | null>(null);
  const [previewingSignature, setPreviewingSignature] = useState<Retirador | null>(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['retiros', otroTramiteId],
    queryFn: () => retiroCopiasService.listarRetiros(otroTramiteId),
    staleTime: 1000 * 60, // 1 minuto
  });

  if (isLoading) {
    return (
      <div className="space-y-3">
        <h4 className="text-sm font-semibold text-gray-700">Historial de Retiros</h4>
        {[1, 2].map((i) => (
          <div key={i} className="rounded-md border border-gray-100 bg-gray-50 p-4">
            <Skeleton className="h-4 w-3/4 mb-2" />
            <Skeleton className="h-3 w-1/2" />
          </div>
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <div className="rounded-lg bg-red-50 border border-red-200 p-4">
        <p className="text-sm text-red-800">Error al cargar el historial de retiros</p>
      </div>
    );
  }

  const retiros = data?.data || [];

  if (retiros.length === 0) {
    return (
      <div>
        <h4 className="text-sm font-semibold text-gray-700 mb-3">Historial de Retiros</h4>
        <div className="rounded-lg bg-gray-50 border border-gray-200 p-6 text-center">
          <Package className="h-12 w-12 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-500">No hay retiros registrados</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h4 className="text-sm font-semibold text-gray-700 mb-3">
        Historial de Retiros ({retiros.length})
      </h4>

      <div className="space-y-3">
        {retiros.map((retiro, index) => (
          <div
            key={retiro.id}
            className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm"
          >
            {/* Header del retiro */}
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 text-blue-700 text-sm font-medium">
                  {index + 1}
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    Retiro
                  </p>
                  <div className="flex items-center gap-1 text-xs text-gray-500">
                    <Calendar className="h-3 w-3" />
                    {formatDate(retiro.fechaRetiro)}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1 text-sm font-medium text-gray-900">
                  <Package className="h-4 w-4 text-gray-500" />
                  {retiro.cantidadTotalRetirada} {retiro.cantidadTotalRetirada === 1 ? 'copia' : 'copias'}
                </div>

                {/* Action buttons */}
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setEditingRetiro(retiro)}
                    className="h-8 w-8 p-0"
                    title="Editar retiro"
                  >
                    <Edit className="h-4 w-4 text-blue-600" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setDeletingRetiro(retiro)}
                    className="h-8 w-8 p-0"
                    title="Eliminar retiro"
                  >
                    <Trash2 className="h-4 w-4 text-red-600" />
                  </Button>
                </div>
              </div>
            </div>

            {/* Lista de retiradores */}
            <div className="space-y-2">
              {retiro.retiradores.map((retirador) => (
                <div
                  key={retirador.id}
                  className="flex items-start gap-3 rounded-md bg-gray-50 p-3 border border-gray-100"
                >
                  <User className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {retirador.nombreCompleto}
                    </p>
                    <p className="text-xs text-gray-500">
                      {retirador.cantidadCopias} {retirador.cantidadCopias === 1 ? 'copia' : 'copias'}
                    </p>
                  </div>
                  {retirador.firmaDigitalUrl && (
                    <div className="flex-shrink-0">
                      <SignatureThumbnailCard
                        signatureUrl={retirador.firmaDigitalUrl}
                        signerName={retirador.nombreCompleto}
                        onClick={() => setPreviewingSignature(retirador)}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Edit Dialog */}
      {editingRetiro && (
        <EditRetiroDialog
          open={Boolean(editingRetiro)}
          onOpenChange={(open) => {
            if (!open) setEditingRetiro(null);
          }}
          retiro={editingRetiro}
          otroTramiteId={otroTramiteId}
          copiasDisponibles={copiasDisponibles}
        />
      )}

      {/* Delete Confirmation Dialog */}
      {deletingRetiro && (
        <DeleteRetiroConfirmDialog
          open={Boolean(deletingRetiro)}
          onOpenChange={(open) => {
            if (!open) setDeletingRetiro(null);
          }}
          retiro={deletingRetiro}
          otroTramiteId={otroTramiteId}
        />
      )}

      <SignaturePreviewDialog
        signatureUrl={previewingSignature?.firmaDigitalUrl}
        open={Boolean(previewingSignature)}
        onOpenChange={(open) => {
          if (!open) {
            setPreviewingSignature(null);
          }
        }}
        title={previewingSignature?.nombreCompleto ?? 'Firma'}
      />
    </div>
  );
};
